import React, { useState, useEffect, useCallback } from 'react';
import { Search, Command, Zap, FileText, Users, DollarSign, Target, Bell, Settings, TrendingUp, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommandPalette = ({ isOpen, onClose }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Quick actions
  const quickActions = [
    { id: 'new-invention', label: 'Create New Invention', icon: Zap, action: () => navigate('/creation/new'), category: 'Actions' },
    { id: 'new-supplier', label: 'Add Supplier', icon: Users, action: () => navigate('/sourcing/suppliers/new'), category: 'Actions' },
    { id: 'view-notifications', label: 'View Notifications', icon: Bell, action: () => navigate('/notifications'), category: 'Navigation' },
    { id: 'view-progress', label: 'View Progress', icon: TrendingUp, action: () => navigate('/progress'), category: 'Navigation' },
    { id: 'dashboard', label: 'Go to Dashboard', icon: Target, action: () => navigate('/dashboard'), category: 'Navigation' },
    { id: 'wealth', label: 'Open Wealth Dashboard', icon: DollarSign, action: () => navigate('/wealth'), category: 'Navigation' },
    { id: 'documents', label: 'View Documents', icon: FileText, action: () => navigate('/documents'), category: 'Navigation' },
    { id: 'settings', label: 'Open Settings', icon: Settings, action: () => navigate('/settings'), category: 'Navigation' },
  ];

  // AI-powered search
  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setResults(quickActions);
      return;
    }

    setLoading(true);
    try {
      // Search across multiple entities
      const [inventions, suppliers, notifications, activities] = await Promise.all([
        axios.get(`${API}/inventions?search=${query}`, { headers: getAuthHeaders() }).catch(() => ({ data: { inventions: [] } })),
        axios.get(`${API}/sourcing/suppliers?search=${query}`, { headers: getAuthHeaders() }).catch(() => ({ data: { suppliers: [] } })),
        axios.get(`${API}/notifications?search=${query}`, { headers: getAuthHeaders() }).catch(() => ({ data: { notifications: [] } })),
        axios.get(`${API}/activity/timeline?search=${query}`, { headers: getAuthHeaders() }).catch(() => ({ data: { activities: [] } }))
      ]);

      const searchResults = [
        // Quick actions matching search
        ...quickActions.filter(action => 
          action.label.toLowerCase().includes(query.toLowerCase())
        ),
        // Inventions
        ...(inventions.data.inventions || []).map(inv => ({
          id: inv.id,
          label: inv.name,
          subtitle: inv.description,
          icon: Zap,
          action: () => navigate(`/creation/${inv.id}`),
          category: 'Inventions'
        })),
        // Suppliers
        ...(suppliers.data.suppliers || []).map(sup => ({
          id: sup.id,
          label: sup.name,
          subtitle: sup.city + ', ' + sup.country,
          icon: Users,
          action: () => navigate(`/sourcing/suppliers/${sup.id}`),
          category: 'Suppliers'
        })),
        // Notifications
        ...(notifications.data.notifications || []).slice(0, 5).map(notif => ({
          id: notif.id,
          label: notif.title,
          subtitle: notif.message,
          icon: Bell,
          action: () => navigate('/notifications'),
          category: 'Notifications'
        })),
      ];

      setResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
      setResults(quickActions);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, performSearch]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % results.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + results.length) % results.length);
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        results[selectedIndex].action();
        onClose();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
      setTimeout(() => {
        document.getElementById('command-palette-search')?.focus();
      }, 100);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Group results by category
  const groupedResults = results.reduce((acc, result) => {
    const category = result.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(result);
    return acc;
  }, {});

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Command Palette */}
      <div className="fixed top-[20%] left-1/2 transform -translate-x-1/2 w-full max-w-2xl z-50 px-4">
        <div className="bg-gray-900 rounded-lg shadow-2xl border border-gray-700 overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b border-gray-700">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              id="command-palette-search"
              type="text"
              placeholder="Search anything... (Type to search, ↑↓ to navigate, Enter to select)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-lg"
              autoComplete="off"
            />
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <kbd className="px-2 py-1 bg-gray-800 rounded">ESC</kbd>
              <span>to close</span>
            </div>
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
                <p className="text-gray-400 mt-2">Searching...</p>
              </div>
            ) : Object.keys(groupedResults).length === 0 ? (
              <div className="p-8 text-center">
                <Search className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-400">No results found</p>
                <p className="text-gray-500 text-sm mt-1">Try a different search term</p>
              </div>
            ) : (
              <div className="py-2">
                {Object.entries(groupedResults).map(([category, items]) => (
                  <div key={category} className="mb-2">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {category}
                    </div>
                    {items.map((result, index) => {
                      const globalIndex = results.indexOf(result);
                      const Icon = result.icon;
                      return (
                        <button
                          key={result.id}
                          onClick={() => {
                            result.action();
                            onClose();
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                            globalIndex === selectedIndex
                              ? 'bg-yellow-500/20 border-l-4 border-yellow-500'
                              : 'hover:bg-gray-800'
                          }`}
                          onMouseEnter={() => setSelectedIndex(globalIndex)}
                        >
                          <Icon className="w-5 h-5 text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-medium truncate">{result.label}</div>
                            {result.subtitle && (
                              <div className="text-gray-400 text-sm truncate">{result.subtitle}</div>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-gray-800 border-t border-gray-700 flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-gray-900 rounded">↑</kbd>
                <kbd className="px-2 py-1 bg-gray-900 rounded">↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-2 py-1 bg-gray-900 rounded">Enter</kbd>
                Select
              </span>
            </div>
            <span className="flex items-center gap-1">
              <Command className="w-3 h-3" />
              <kbd className="px-2 py-1 bg-gray-900 rounded">K</kbd>
              to open
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
